import { UUID } from "../../../../../../core/pojo/id-variable";
import { bufferTime, Subject, Subscription } from "rxjs";
import { Organization } from "../../../../../organization/dto/Organization";
import { KpiBusinessService } from "../../../../service/backend/kpi-business.service";
import { MetricMiniWithCompanies } from "../../../../dto/Metric";
import {
  SelectorAutocompletePickerWrapper,
} from "../../../../../shared/component/input/selector-autocomplete/selector-autocomplete.component";
import { filter, map, mergeMap, tap, distinct } from "rxjs/operators";

/**
 * Manages efficient retrieval of company counts for metrics through request batching,
 * caching, and deferred resolution.
 *
 * This utility class optimizes performance when multiple company count requests
 * are made in rapid succession by:
 *
 * 1. Batching requests within 200ms windows to reduce API calls
 * 2. Caching results for 2 minutes to avoid redundant network requests
 *
 * ## Usage Examples:
 *
 * ### Setup:
 * ```typescript
 * // Initialize in component
 * private companyAmountHelper = new DeferredCompanyAmountHelper(this.kpiService);
 *
 * ngOnInit(): void {
 *   this.companyAmountHelper.selectedOrg = this.selectedOrg;
 *   this.companyAmountHelper.setupMetricBatching();
 * }
 *
 * ngOnDestroy(): void {
 *   this.companyAmountHelper.cancel();
 * }
 * ```
 *
 * ### Requesting company counts:
 * ```typescript
 * async updateMetricInfo(selectorItem: SelectorAutocompletePickerWrapper, metric: MetricMiniWithCompanies): Promise<void> {
 *   await this.companyAmountHelper.getAmountOfCompaniesWithData(selectorItem, metric);
 *   // At this point, metric.companiesAmount is populated and selectorItem.comment is updated
 * }
 * ```
 *
 * ### Displaying company counts:
 * ```html
 * <div class="metric-info">
 *   {{metric.name}} - {{DeferredCompanyAmountHelper.companiesToComment(metric)}}
 * </div>
 * ```
 *
 * The helper handles all the complexity of request timing, batching, and caching
 * while providing a simple promise-based interface for components to use.
 */
export class DeferredCompanyAmountHelper {
  private companiesAmountCache: CompaniesAmountCache = new Map<UUID, { time: number, result: number }>();
  private metricBatchSubject = new Subject<{ uuid: UUID, resolver: (count: number) => void }>();
  private batchSubscription: Subscription;

  selectedOrg: Organization;

  constructor(private kpiService: KpiBusinessService) {
  }

  cancel(): void {
    if (this.batchSubscription) {
      this.batchSubscription.unsubscribe();
    }
  }

  static companiesToComment(metricMiniGroup: MetricMiniWithCompanies): string {
    switch (metricMiniGroup.companiesAmount) {
      case undefined:
      case 0:
        return '';
      case 1:
        return '1 company';
      default:
        return metricMiniGroup.companiesAmount.toString() + ' companies';
    }
  }


  getAmountOfCompaniesWithData(item: SelectorAutocompletePickerWrapper, metric: MetricMiniWithCompanies): Promise<void> {
    return new Promise<void>((resolve: () => void) => {
      if (!metric || !metric.metric || !metric.metric.uuid) {
        resolve();
        return;
      }
      const now = Date.now();
      const cacheResult = this.companiesAmountCache.get(metric.metric.uuid);
      if (cacheResult && (now - cacheResult.time) < MAX_CACHE_LIFETIME) {
        metric.companiesAmount = cacheResult.result;
        item.comment = DeferredCompanyAmountHelper.companiesToComment(metric);
        resolve();
        return;
      }
      // Push this request to the batch subject
      this.metricBatchSubject.next({
        uuid: metric.metric.uuid,
        resolver: (count: number) => {
          // Update the item with the count
          if (item && typeof item === 'object') {
            metric.companiesAmount = count;
            item.comment = DeferredCompanyAmountHelper.companiesToComment(metric);
            this.companiesAmountCache.set(metric.metric.uuid, { time: now, result: metric.companiesAmount });
          }
          resolve();
        },
      });
    });
  }

  setupMetricBatching(): void {
    this.batchSubscription = this.metricBatchSubject.pipe(
      distinct((v) => v.uuid),
      // Collect requests for some time
      bufferTime(SCROLL_BUFFER_TIME),
      // Only process if we have items
      filter((batch) => batch.length > 0),
      // Convert batch to a map of UUID to resolvers
      map((batch) => {
        const uuids: UUID[] = [];
        const resolverMap = new Map<UUID, ((count: number) => void)>();

        batch.forEach((item) => {
          uuids.push(item.uuid);

          resolverMap.set(item.uuid, item.resolver);
        });

        return { uuids, resolverMap };
      }),
      // Make batch request, not cancelling current one
      mergeMap(({ uuids, resolverMap }) => {
        return this.kpiService.getBatchAmountOfCompaniesWithData(this.selectedOrg.uuid, uuids).pipe(
          // Distribute results to resolvers
          tap((results) => {
            Object.entries(results).forEach(([uuid, count]) => {
              const resolver = resolverMap.get(uuid);
              resolver(count);
            });
          })
        );
      })
    ).subscribe();
  }
}

const MAX_CACHE_LIFETIME = 1000 * 60 * 2; // 2 minutes
const SCROLL_BUFFER_TIME = 300; // 300ms
type CompaniesAmountCache = Map<UUID, { time: number, result: number }>;
